import { xxHash } from "@flakiness/shared/common/utils.js";
import { SyncComputationCache } from "../computationCache.js";
import { WireTypes } from "../wireTypes.js";
import { CommitAnalyzer, ContributionReport, createCommitContributionReport, createWorstContributionReport } from "./commitAnalyzer.js";
import { EnvGroup } from "./envGroup.js";
import { Histogram as H } from './histogram.js';
import { ResolvedTimeline } from "./resolvedTimeline.js";
import { TestOutcomes as T } from './testOutcomes.js';

/**
 * Span Analyzer holds an ordered set of commits and computes data
 * for them.
 */

export type SpanStats = {
  testOutcomes: T.TestOutcomes,
  durations: H.Histogram[],
  commitsCount: number,
}

export class SpanAnalyzer {
  static etag(commits: CommitAnalyzer[]): string {
    return xxHash(commits.map(c => c.etag()));
  }

  private _etag: string;

  private _contributions = new SyncComputationCache<{ timeline: ResolvedTimeline, classification: WireTypes.OutcomeClassification }, ContributionReport>({
    size: EnvGroup.MAX_TIMELINES * WireTypes.OUTCOME_CLASSIFICATIONS.length,
    etag: ({ timeline, classification }) => xxHash([timeline.etag(), classification]),
    compute: ({ timeline, classification }) => {
      const contribs = this.commits.map(commit => commit.contributions(timeline)).flat();
      return classification === 'worst' ? createWorstContributionReport(contribs) : createCommitContributionReport(contribs);
    }
  });

  private _testOutcomes = new SyncComputationCache<{ timeline: ResolvedTimeline, classification: WireTypes.OutcomeClassification }, T.TestOutcomes>({
    size: EnvGroup.MAX_TIMELINES * WireTypes.OUTCOME_CLASSIFICATIONS.length,
    etag: ({ timeline, classification }) => xxHash([timeline.etag(), classification]),
    compute: ({ timeline, classification }) => {
      return T.unionAll(this._contributions.get({ timeline, classification }).map(c => c.outcomes));
    }
  });

  constructor(public readonly commits: CommitAnalyzer[], etag: string = SpanAnalyzer.etag(commits)) {
    this._etag = etag;
  }

  etag() {
    return this._etag;
  }

  isEmpty() {
    return this.commits.length === 0;
  }

  /**
   * The contributions that give every test its outcome: exactly one per test, so their
   * durations, errors and tags describe the run the outcome came from.
   */
  contributions(timeline: ResolvedTimeline, classification: WireTypes.OutcomeClassification): ContributionReport {
    return this._contributions.get({ timeline, classification });
  }

  /**
   * The outcomes of the tests over the commits span. With `eod` every test gets its last
   * outcome, with `worst` the worst one it had anywhere in the span.
   */
  testOutcomes(timeline: ResolvedTimeline, classification: WireTypes.OutcomeClassification) {
    return this._testOutcomes.get({ timeline, classification });
  }
}